/**
 * How wet the ground is after rain (ADR-144 build note, P2.27-T20): one number, the wet minutes, that rises while it rains
 * and falls while it does not. The ground darkens and puddles form and dry as functions of it, so a storm, which adds minutes
 * twice as fast, forms them twice as fast, and drying, being the same number running back, shrinks the puddles that formed
 * last first. Minutes are real minutes, so every page of a region reads the same wetness from the clock alone. Pure.
 */

/**
 * How many milliseconds one wet minute is: a real minute.
 */
export const WET_MINUTE_MS = 60_000;

/**
 * The most wet minutes the ground holds: the latest a large puddle starts plus its span, so a page that has rained long
 * enough starts drying with every puddle full.
 */
export const WET_MAX = 27;

/**
 * The wet minutes at which the ground starts to darken, the minutes of rain after that it is soaked in, and the minutes of
 * clear sky it takes to dry from soaked to dry.
 */
export const SOAK_FROM = 1;
export const SOAK_FULL = 3;
export const SOAK_DRY_MINUTES = 60;

/**
 * The size classes of a puddle.
 */
export type PuddleClass = "small" | "medium" | "large";

/**
 * When a class of puddle appears and how long it takes to grow: its onset is drawn between `from` and `to` wet minutes and
 * it is full `grow` minutes later.
 */
export interface PuddleTiming {
  readonly from: number;
  readonly to: number;
  readonly grow: number;
}

/**
 * The timings of the three classes (the author's ruling of 2026-10-31, in real minutes of steady rain).
 */
export const PUDDLE_TIMINGS: Readonly<Record<PuddleClass, PuddleTiming>> = {
  small: { from: 4, to: 6, grow: 3 },
  medium: { from: 8, to: 12, grow: 5 },
  large: { from: 15, to: 20, grow: 7 },
};

const LOOKBACK_MS = 150 * WET_MINUTE_MS;
const REPLAY_STEP_MS = 20_000;

/**
 * What the ground holds: one tank of wet minutes for each class of puddle, which decide how far that class's puddles have
 * formed, the minutes of drying, and how soaked the ground is, which decides its tint. The tanks fill while it rains and keep
 * what they hold; the drying clock runs while it does not and runs back while it rains, and each class's puddles shrink
 * along it in turn, so large puddles shrink first and small ones last. The soak fades across the whole drying.
 */
export interface WetState {
  small: number;
  medium: number;
  large: number;
  dry: number;
  soak: number;
}

/**
 * How a class dries: the minutes after the rain stops that its puddles wait, and the minutes they then take to be gone.
 */
export interface DryingTiming {
  readonly delay: number;
  readonly span: number;
}

/**
 * The drying of the three classes, in real minutes: large puddles shrink from the moment the rain stops and are gone after
 * about 40 to 48, medium ones between 10 and 53, small ones between 25 and 62 (each puddle's own span varies by a third either side).
 */
export const DRYING: Readonly<Record<PuddleClass, DryingTiming>> = {
  large: { delay: 0, span: 36 },
  medium: { delay: 10, span: 32 },
  small: { delay: 25, span: 28 },
};

/**
 * How many minutes of drying a minute of steady rain wets back.
 */
export const REWET_RATE = 3;

const CLASS_KEYS: readonly PuddleClass[] = ["small", "medium", "large"];

/**
 * A new state of dry ground.
 *
 * @returns The state.
 */
export function dryGround(): WetState {
  return { small: 0, medium: 0, large: 0, dry: 0, soak: 0 };
}

/**
 * The most wet minutes a class's tank holds: the latest its puddles start plus their span.
 *
 * @param cls The class.
 * @returns The wet minutes.
 */
export function tankMax(cls: PuddleClass): number {
  const timing = PUDDLE_TIMINGS[cls];
  return timing.to + timing.grow;
}

/**
 * Moves the state forward in place. While it rains every tank rises by the strength each minute, the drying clock runs back
 * by `REWET_RATE` times that, and the soak rises over `SOAK_FULL - SOAK_FROM` minutes of rain once the small tank passes
 * `SOAK_FROM`; while it does not, the drying clock runs and the soak falls over `SOAK_DRY_MINUTES`.
 *
 * @param state The state, written.
 * @param strength How much weather falls, 0 clear, 1 rain, 2 storm (`weatherStrength`).
 * @param dtMs The time that passes in milliseconds.
 * @returns `state`.
 */
export function advanceWet(state: WetState, strength: number, dtMs: number): WetState {
  const minutes = dtMs / WET_MINUTE_MS;
  if (strength > 0) {
    const before = state.small;
    for (const cls of CLASS_KEYS) {
      const next = state[cls] + strength * minutes;
      const top = tankMax(cls);
      state[cls] = next >= top ? top : next;
    }
    const dry = state.dry - strength * minutes * REWET_RATE;
    state.dry = dry <= 0 ? 0 : dry;
    if (state.small > SOAK_FROM) {
      const earned = Math.min(state.small - Math.max(before, SOAK_FROM), strength * minutes);
      const soak = state.soak + Math.max(0, earned) / (SOAK_FULL - SOAK_FROM);
      state.soak = soak >= 1 ? 1 : soak;
    }
    return state;
  }
  state.dry += minutes;
  const soak = state.soak - minutes / SOAK_DRY_MINUTES;
  state.soak = soak <= 0 ? 0 : soak;
  return state;
}

/**
 * The wet minutes at which one puddle appears.
 *
 * @param cls The puddle's class.
 * @param unit A number from 0 to 1 the puddle's seed gives.
 * @returns A value between the class's `from` and `to`.
 */
export function puddleOnset(cls: PuddleClass, unit: number): number {
  const timing = PUDDLE_TIMINGS[cls];
  return timing.from + (timing.to - timing.from) * unit;
}

/**
 * How full a puddle is: 0 before its onset, 1 a class span after it, then shrinking to nothing along the class's drying.
 *
 * @param state The ground's state.
 * @param cls The puddle's class.
 * @param unit The puddle's seeded number from 0 to 1 (`puddleOnset`).
 * @returns 0 to 1.
 */
export function puddleFill(state: WetState, cls: PuddleClass, unit: number): number {
  const formed = (state[cls] - puddleOnset(cls, unit)) / PUDDLE_TIMINGS[cls].grow;
  if (formed <= 0) return 0;
  const timing = DRYING[cls];
  const gone = (state.dry - timing.delay) / (timing.span * (0.67 + 0.66 * unit));
  const left = gone <= 0 ? 1 : gone >= 1 ? 0 : 1 - gone;
  const fill = (formed >= 1 ? 1 : formed) * left;
  return fill;
}

/**
 * The state after steady rain has fallen for a number of real minutes on dry ground.
 *
 * @param minutes Real minutes of steady rain.
 * @returns The state.
 */
export function wetAfterRain(minutes: number): WetState {
  return advanceWet(dryGround(), 1, minutes * WET_MINUTE_MS);
}

/**
 * The state a number of real minutes after steady rain that soaked the ground fully stopped.
 *
 * @param minutes Real minutes of clear sky since.
 * @returns The state.
 */
export function wetAfterStop(minutes: number): WetState {
  return advanceWet({ small: tankMax("small"), medium: tankMax("medium"), large: WET_MAX, dry: 0, soak: 1 }, 0, minutes * WET_MINUTE_MS);
}

/**
 * The state at a time, replayed from the weather before it: the strength is stepped from 150 real minutes back, enough for
 * the ground to have dried or soaked fully whatever it began as, so every page reads the same state from the clock.
 *
 * @param strengthAt The weather's strength at a time in milliseconds (`weatherStrengthAt`).
 * @param epochMs The time to read.
 * @returns The state.
 */
export function wetStateAt(strengthAt: (epochMs: number) => number, epochMs: number): WetState {
  const state = dryGround();
  let t = epochMs - LOOKBACK_MS;
  while (t < epochMs) {
    const step = Math.min(REPLAY_STEP_MS, epochMs - t);
    advanceWet(state, strengthAt(t + step / 2), step);
    t += step;
  }
  return state;
}

/**
 * Mixes two ground states field by field, as a page eases its ground from one region's weather to another's.
 *
 * @param from The state at a mix of 0.
 * @param to The state at a mix of 1.
 * @param t The mix, clamped to 0 to 1.
 * @param out The state written; returned.
 * @returns `out`.
 */
export function blendWetInto(from: Readonly<WetState>, to: Readonly<WetState>, t: number, out: WetState): WetState {
  const k = t <= 0 ? 0 : t >= 1 ? 1 : t;
  out.small = from.small + (to.small - from.small) * k;
  out.medium = from.medium + (to.medium - from.medium) * k;
  out.large = from.large + (to.large - from.large) * k;
  out.dry = from.dry + (to.dry - from.dry) * k;
  out.soak = from.soak + (to.soak - from.soak) * k;
  return out;
}
